import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, map, mergeMap, take } from 'rxjs';
import { SpecialCharacters, UserRoles } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import {
  DEFAULT_LANGUAGE,
  Language,
} from 'src/app/core/models/languages.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { RolePermissionsUtils } from 'src/app/core/utils/role-permissions/role-permissions.utils';
import { IOption } from '../../form/components/form-select/form-select.component';
import { DropdownPosition } from '../../form/components/form-select/form-select.constants';
import {
  IMAGE_SRC,
  STATUS_OPTIONS,
  VALIDATION_MESSAGE,
} from '../../shared-constants';
import {
  ACTIVE_USER_OPTIONS,
  CONSTANTS,
  FORM_STATUS,
  IDS,
  LANGUAGE_DEFAULT,
  LANGUAGES,
  ProfileData,
} from './profile-modal.constants';

@Component({
  selector: 'app-profile-modal',
  templateUrl: './profile-modal.component.html',
  styleUrls: ['./profile-modal.component.scss'],
  standalone: false,
})
export class ProfileModalComponent implements OnInit {
  readonly commonUtils = inject(CommonUtils);
  readonly userService = inject(UserService);

  userProfileForm!: FormGroup;
  model!: UserAdmin;
  modalStatus!: FORM_STATUS;
  showButton!: boolean;
  modalHeader!: string;

  currentUser!: UserAdmin;

  destroy = inject(DestroyRef);

  locationGroups: IOption[] = [];
  linkedSites: IOption[] = [];
  disableEmailRole = false;
  roleOptions: IOption[] = [];

  readonly LANGS = [
    LANGUAGE_DEFAULT,
    ...LANGUAGES.slice().sort((a, b) =>
      a.label.toString().localeCompare(b.label.toString()),
    ),
  ];
  readonly CONST = CONSTANTS;
  readonly ERROR = VALIDATION_MESSAGE;
  readonly USER_OPTIONS = ACTIVE_USER_OPTIONS;
  readonly IMAGES = IMAGE_SRC;
  readonly CHARS = SpecialCharacters;
  readonly IDS = IDS;
  readonly FORM_STATUS = FORM_STATUS;
  readonly IMAGE_OPTIONS = STATUS_OPTIONS;
  readonly DropdownPosition = DropdownPosition;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: ProfileData,
    private dialogRef: MatDialogRef<ProfileModalComponent>,
    private locationService: LocationService,
    private fb: FormBuilder,

    private readonly roleUtils: RolePermissionsUtils,
  ) {}

  /**
   * Angular lifecycle hook that initializes the component.
   *
   * Behavior:
   * - Retrieves `UserService` from the service provider.
   * - Subscribes to `getCurrentUser$()` to obtain the logged-in user's details.
   * - Once the user is available:
   *    - Stores the user in `currentUser`.
   *    - Initializes component data (`initializeData()`).
   *    - Builds or restores the reactive form (`initializeForm()`).
   *    - Fetches location info (`getLocationInfo$()`).
   *    - Loads available roles (`getRoles()`).
   *    - Subscribes to form field changes (`subscribeToFields()`).
   *
   * Uses `takeUntil(this.destroy$)` to ensure subscriptions are cleaned up on component destroy.
   */
  ngOnInit() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((user) => {
          this.currentUser = user;
          this.initializeData();
          this.initializeForm();
          this.getLocationInfo$();
          this.getRoles();
          this.subscribeToFields();
        }),
      )
      .subscribe();
  }

  /**
   * Subscribes to changes in the Employee ID field and updates the User ID field accordingly.
   *
   * Behavior:
   * - Watches `EMPLOYEE_ID` form control for value changes.
   * - If Employee ID is cleared:
   *    - Resets User ID and updates its validity.
   * - If Employee ID has a value:
   *    - Extracts the last 10 characters and sets them as the User ID.
   *
   * Uses `distinctUntilChanged` to avoid unnecessary updates when trimmed values are equal.
   */
  subscribeToFields(): void {
    this.userProfileForm
      .get([IDS.EMPLOYEE_ID])
      ?.valueChanges.pipe(
        distinctUntilChanged((prev, curr) => prev?.trim() === curr?.trim()),
      )
      .subscribe((currentValue: string) => {
        const userIdControl = this.userProfileForm.get(IDS.USER_ID);

        if (!currentValue?.trim()) {
          // If Employee ID is empty, clear User ID
          userIdControl?.reset();
          userIdControl?.updateValueAndValidity();
          return;
        }

        // Otherwise, copy last 10 characters
        const last10 = currentValue.slice(-10);
        userIdControl?.setValue(last10);
        userIdControl?.updateValueAndValidity();
      });
  }

  /**
   * Handles button click actions based on the current modal status.
   *
   * Behavior:
   * - If the modal is in `VIEW_EDIT` mode:
   *    - Switches to `EDIT` mode and reinitializes the form for editing.
   * - Otherwise:
   *    - Calls `onSubmit()` to process the form submission.
   */
  onButtonClick() {
    if (this.modalStatus === FORM_STATUS.VIEW_EDIT) {
      this.modalStatus = FORM_STATUS.EDIT;
      this.initializeForm();
    } else {
      this.onSubmit();
    }
  }

  /**
   * Handles the exit action from the modal.
   *
   * Behavior:
   * - Closes the dialog and passes:
   *    - Form data formatted for the request,
   *    - `onExit: true` to indicate exit intent,
   *    - Current form status,
   *    - `untouched` flag based on form pristine state.
   *
   * Used to trigger exit confirmation logic in the parent component.
   */
  onExit(): void {
    this.commonUtils.openExitModal(this.userProfileForm.dirty);
  }

  /**
   * Updates linked site options when the selected group changes.
   *
   * Behavior:
   * - Calls `getLinkedSitesInfo$()` with the selected group value.
   * - On completion, resets the Location field to ensure consistency.
   *
   * Uses `take(1)` to auto-complete after the first emission.
   */
  updateLinkedSites(): void {
    this.getLinkedSitesInfo$(this.userProfileForm.get(IDS.GROUP)?.value)
      .pipe(take(1))
      .subscribe(() => {
        this.userProfileForm.get(IDS.LOCATION)?.reset();
      });
  }

  /**
   * Initializes the reactive form for the user profile modal.
   *
   * Behavior:
   * - If `dialogData.form` exists:
   *    - Reuses the provided form instance.
   * - Otherwise:
   *    - Creates a new form group with controls for:
   *      User ID, Employee ID, Email, Last Name, First Name, Role, Group, Location, Language, Active status.
   *    - Populates initial values from `model` or `currentUser`.
   *    - Applies required validators to all fields.
   *
   * Additional logic:
   * - In EDIT mode:
   *    - Disables User ID, Employee ID, Email fields.
   *    - Handles `disableEmailRole` flag.
   *    - Marks all fields dirty/touched if reopened.
   * - In ADD mode:
   *    - Adds email format validator.
   * - For reopened modals:
   *    - Marks form as dirty/touched and updates validity.
   *
   * Finally:
   * - Subscribes to Employee ID changes to auto-update User ID.
   */
  initializeForm(): void {
    if (this.dialogData?.form) {
      this.userProfileForm = this.dialogData.form;
    } else {
      this.userProfileForm = this.fb.group({
        [IDS.USER_ID]: [
          this.model?.loginId || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.EMPLOYEE_ID]: [
          this.model?.employeeId || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.EMAIL]: [
          this.model?.email || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.LAST_NAME]: [
          this.model?.familyName || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.FIRST_NAME]: [
          this.model?.givenName || '',
          Validators.compose([Validators.required]),
        ],
        [IDS.USER_ROLE]: [
          this.model?.roleName || UserRoles.USER,
          Validators.compose([Validators.required]),
        ],
        [IDS.GROUP]: [
          this.model?.locationGroup || this.currentUser?.locationGroup,
          Validators.compose([Validators.required]),
        ],
        [IDS.LOCATION]: [
          this.model?.location || this.currentUser?.location,
          Validators.compose([Validators.required]),
        ],
        [IDS.LANGUAGE]: [
          this.model?.defaultLanguage || this.LANGS[0].label,
          Validators.compose([Validators.required]),
        ],
        [IDS.ACTIVE]: [
          this.model?.enabled || STATUS_OPTIONS.ACTIVE,
          Validators.compose([Validators.required]),
        ],
      });
    }

    if (this.modalStatus === FORM_STATUS.EDIT) {
      this.userProfileForm.get(IDS.USER_ID)?.disable();
      this.userProfileForm.get(IDS.EMPLOYEE_ID)?.disable();
      this.userProfileForm.get(IDS.EMAIL)?.disable();
      this.userProfileForm.get(IDS.LOCATION)?.setValue(this.model.location);

      if (this.disableEmailRole) {
        this.userProfileForm.get(IDS.EMAIL)?.disable();
      }

      if (this.dialogData?.isReOpened) {
        this.userProfileForm.markAllAsDirty();
        this.userProfileForm.markAllAsTouched();
      }
    }

    if (this.modalStatus === FORM_STATUS.ADD) {
      // Add required email validator when adding a User
      this.userProfileForm.get(IDS.EMAIL)?.addValidators(Validators.email);
    }

    // For reopened modal
    if (this.dialogData?.isReOpened) {
      this.userProfileForm.markAsDirty();
      this.userProfileForm.markAllAsTouched();
      this.userProfileForm.updateValueAndValidity();
    }

    this.subscribeToFields();
  }

  /**
   * Fetches all available user roles from the backend and populates `roleOptions`.
   *
   * Behavior:
   * - Obtains `UserService` from `serviceProvider`.
   * - Calls `getAllRoles()` and transforms the response into `{ label, value }` options.
   * - Sorts the options alphabetically by `label` for stable UI ordering.
   *
   * Cleanup:
   * - Uses `takeUntil(this.destroy$)` to avoid memory leaks on component teardown.
   */
  getRoles() {
    this.userService
      .getAllRoles()
      .pipe(
        takeUntilDestroyed(this.destroy),
        map((roles) => {
          // Add to User Role Dropdown
          roles.response.roles.forEach((role) => {
            this.roleOptions.push({
              label: role.roleName,
              value: role.roleName,
            });
          });
          this.roleOptions.sort((a, b) =>
            a.label.toString().localeCompare(b.label.toString()),
          );
        }),
      )
      .subscribe();
  }

  /**
   * Trims leading/trailing whitespace from the value of the specified form control.
   *
   * @param id - The form control key (from `IDS`) to trim.
   *
   * Side effects:
   * - Mutates the form control value by replacing it with its trimmed version.
   * - Trimming helps enforce clean values before validation or submission.
   */
  trim(id: string): void {
    const control = this.userProfileForm.get(id);
    control?.setValue(control.value.trim());
  }

  /**
   * Indicates whether the primary action button should be disabled.
   *
   * Rules by modal status:
   * - `VIEW`: button is enabled (`false`), typically used to toggle to edit mode.
   * - `ADD` / `EDIT`: disabled if the form is invalid or pristine (no changes yet).
   * - Default: `false`.
   *
   * @returns `true` to disable the button; `false` to enable.
   */
  get isButtonDisabled(): boolean {
    switch (this.modalStatus) {
      case FORM_STATUS.VIEW:
        return false;
      case FORM_STATUS.ADD:
      case FORM_STATUS.EDIT:
        return this.userProfileForm?.invalid || this.userProfileForm?.pristine;
      default:
        return false;
    }
  }

  /**
   * Returns the display label for the selected language in the form.
   *
   * Behavior:
   * - If the selected language equals the first `LANGS` label, return that label directly.
   * - Otherwise, find the matching entry by `value` and return the corresponding `label`.
   *
   * @returns The human-readable language label, or `undefined` if not found.
   */
  get viewLanguage() {
    if (this.userProfileForm.get(IDS.LANGUAGE)?.value === this.LANGS[0].label) {
      return this.LANGS[0].label;
    } else {
      return this.LANGS.find(
        (lang) => lang.value === this.userProfileForm.get(IDS.LANGUAGE)?.value,
      )?.label;
    }
  }

  /**
   * Indicates whether the User Role field should be disabled.
   *
   * Disabled when:
   * - The current user cannot edit admin-search users (`roleUtils.canEditAdminSearchUsers === false`), OR
   * - The dialog is opened for the current user's own profile (`isOwnProfile === true`).
   *
   * @returns `true` to disable the User Role control; otherwise `false`.
   */
  get isUserRoleDisabled(): boolean {
    return (
      !this.roleUtils.canEditAdminSearchUsers(this.currentUser?.roleName) ||
      this.dialogData.isOwnProfile === true
    );
  }

  get canEditGroup(): boolean {
    return this.dialogData.isOwnProfile
      ? !this.roleUtils.canChangeOwnGroup(this.currentUser?.roleName)
      : false;
  }

  get canEditLocation(): boolean {
    return this.dialogData.isOwnProfile
      ? !this.roleUtils.canChangeOwnLocation(this.currentUser?.roleName)
      : false;
  }

  /**
   * Submits the form by closing the dialog with a SAVE payload.
   *
   * Behavior:
   * - Formats the form into a backend-friendly `UserAdmin` object via `formatDataForRequest()`.
   * - Closes the modal with `onExit: false` to signal a SAVE action to the caller.
   * - Includes the current `formStatus` to indicate the operation (ADD/EDIT).
   *
   * Note:
   * - The parent component should handle this close event and perform the API call.
   */
  private onSubmit(): void {
    this.dialogRef.close({
      data: this.formatDataForRequest(),
      onExit: false,
      formStatus: this.modalStatus,
    });
  }

  /**
   * Initializes modal-bound data and normalizes backend values for display.
   *
   * Behavior:
   * - Loads `model`, `modalStatus`, and UI flags from `dialogData`.
   * - Sets defaults for optional flags (`disableEmailRole`, `showButton`, `modalHeader`).
   * - Normalizes backend values to user-friendly representations:
   *   - Converts `defaultLanguage` (e.g., `DEFAULT_LANGUAGE`) to the first `LANGS` label.
   *   - Maps `enabled` booleans to `ACTIVE_USER_OPTIONS` labels.
   *
   * Side effects:
   * - Mutates `model` fields to their normalized equivalents for UI display.
   */
  private initializeData(): void {
    this.model = this.dialogData?.data;
    this.modalStatus = this.dialogData?.formStatus;
    this.disableEmailRole =
      this.dialogData?.disableEmailRole === undefined
        ? false
        : this.dialogData?.disableEmailRole;
    this.showButton =
      this.dialogData?.showButton === undefined
        ? true
        : this.dialogData?.showButton;
    this.modalHeader =
      this.dialogData?.modalHeader === undefined
        ? CONSTANTS.HEADER
        : this.dialogData?.modalHeader;

    // Replace BE values with values that make more sense to user
    if (this.model?.defaultLanguage === DEFAULT_LANGUAGE) {
      this.model.defaultLanguage = this.LANGS[0].label as Language;
    }

    if (this.model?.enabled === true) {
      this.model.enabled = ACTIVE_USER_OPTIONS[0].label as boolean;
    }

    if (this.model?.enabled === false) {
      this.model.enabled = ACTIVE_USER_OPTIONS[1].label as boolean;
    }
  }

  /**
   * Formats the reactive form values into a `UserAdmin` payload expected by the backend.
   *
   * Mapping:
   * - Copies raw form values to domain fields (userId → loginId, etc.).
   * - Converts `defaultLanguage` from label to `value` using `LANGS`.
   * - Converts `active` from dropdown label to boolean via `ACTIVE_USER_OPTIONS`,
   *   and also accepts `STATUS_OPTIONS` variants (`ACTIVE`/`DEACTIVATED`).
   * - Preserves `version` from the existing `model` (BE increments).
   * - Sets `userType` placeholder ('H')—not expected to be required long-term.
   *
   * @returns A well-formed `UserAdmin` object ready for API submission.
   */
  private formatDataForRequest(): UserAdmin {
    const formData = this.userProfileForm.getRawValue();

    const lang =
      this.LANGS.find(
        (language) =>
          (language.label as string)?.toLowerCase() ===
          formData.lang.toLowerCase(),
      )?.value || formData.lang;

    let active =
      ACTIVE_USER_OPTIONS.find(
        (activeUser) => activeUser.label === formData.active,
      )?.value || formData.active;

    if (active === STATUS_OPTIONS.ACTIVE) {
      active = true;
    }

    if (active === STATUS_OPTIONS.DEACTIVATED) {
      active = false;
    }

    const data: UserAdmin = {
      ...this.model,
      // maintain version; backend will increment
      version: this.model?.version,
      loginId: formData.userId,
      employeeId: formData.employeeId,
      familyName: formData.lastName,
      givenName: formData.firstName,
      email: formData.email,
      enabled: active,
      location: formData.location,
      locationGroup: formData.group,
      // Placeholder; expected to be removed if BE no longer requires it
      userType: 'H',
      defaultLanguage: lang as Language,
      roleName: formData.userRole,
    };

    return data;
  }

  /**
   * Loads location groups and their linked sites, and initializes the UI lists.
   *
   * Behavior:
   * - Calls `locationService.getLocationGroups()` and builds `locationGroups` options.
   * - Determines the initial group to load linked sites from:
   *   - `model.locationGroup`, or
   *   - `currentUser.locationGroup`, or
   *   - First group in the response.
   * - Calls `getLinkedSitesInfo$(linkedSites)` to populate `linkedSites`.
   *
   * Cleanup:
   * - Uses `take(1)` for single emission processing.
   */
  private getLocationInfo$(): void {
    this.locationService
      .getLocationGroups()
      .pipe(
        take(1),
        mergeMap((data) => {
          const groups = data?.response?.locationGroups;
          groups.forEach((group) => {
            this.locationGroups.push({ label: group.code, value: group.code });
          });
          this.locationGroups.sort((a, b) =>
            a.label.toString().localeCompare(b.label.toString()),
          );

          let linkedSites = groups[0]?.code;
          if (this.model?.locationGroup !== undefined) {
            linkedSites = this.model?.locationGroup;
          } else if (this.currentUser) {
            linkedSites = this.currentUser?.locationGroup;
          }

          return this.getLinkedSitesInfo$(linkedSites);
        }),
      )
      .subscribe();
  }

  /**
   * Loads the linked site list for a given location group and updates `linkedSites`.
   *
   * @param group - The location group code to query (e.g., 'NYC-GRP').
   *
   * Behavior:
   * - Calls `locationService.getLinkedSitesByGroup(group)`.
   * - Transforms response into `{ label, value }` options for UI.
   * - Sorts options alphabetically by `label`.
   *
   * Cleanup:
   * - Uses `take(1)` to process a single response.
   *
   * @returns An observable completing after the list is updated.
   */
  private getLinkedSitesInfo$(group: string) {
    return this.locationService.getLinkedSitesByGroup(group).pipe(
      take(1),
      map((dat) => {
        this.linkedSites = [];
        dat?.response?.locations?.forEach((site) => {
          this.linkedSites.push({ label: site.code, value: site.code });
        });
        this.linkedSites.sort((a, b) =>
          a.label.toString().localeCompare(b.label.toString()),
        );
      }),
    );
  }
}
